<script>
import _ from "lodash"

import ApiService from "@/lib/api_service"

import Chat from "./chat/index.vue"

import { mapActions } from "pinia"
import { useChatStore } from "@/store/chatStore"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

import { Button } from "@/components/ui/button"

import MenuIcon from "./chat/assets/menu_icon.vue"

export default {
  name: "ChatDrawer",

  components: {
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenu,
    Button,
    Chat,
    MenuIcon
  },

  props: {
    isFolder: Boolean,

    datasourceName: String,

    path: String,

    selectedFile: {
      type: Object,
      default() {
        return {}
      }
    }
  },

  data() {
    return {
      drawerActions: [
        {
          icon: "fa fa-refresh",
          label: this.$t("commons.zapdoc.reload_chat"),
          action: this.reloadChat
        },
        {
          icon: "fa fa-trash",
          label: this.$t("commons.zapdoc.delete_chat"),
          action: () => {
            this.isDeleting = true
            this.deleteChat(this.chatKey, this.reloadChat)
          }
        }
      ],
      chatKey: null,
      docIds: {},
      isLoadingKey: false,
      isLoadingDocIds: false,
      isDeleting: false,
      error: null,
      drawerActionsIsOpen: false
    }
  },

  computed: {
    isLoading() {
      return this.isLoadingKey || this.isLoadingDocIds || this.isDeleting
    },

    selectionPresent() {
      return !_.isEmpty(this.selectedFile)
    },

    selectedFileType() {
      return this.isFolder ? "folder" : "file"
    },

    currentNode() {
      return _.get(this.selectedFile, "id")
    },

    title() {
      return _.get(this.selectedFile, "title")
    }
  },

  watch: {
    currentNode: {
      handler(newValue) {
        if (newValue) {
          this.fetchChatKey()
        }
      },
      immediate: true
    },

    chatKey(newValue) {
      if (newValue && !this.error) this.fetchChatDocIds(newValue)
    }
  },

  methods: {
    ...mapActions(useChatStore, ["deleteChat"]),

    fetchChatKey() {
      this.isLoadingKey = true
      this.error = null

      ApiService.fetchChatKey(
        {
          datasource_name: this.datasourceName,
          path: this.path,
          current_node: this.currentNode
        },
        (response) => {
          this.chatKey = response
          this.isLoadingKey = false
        },
        (error) => {
          console.error("Error fetching chat key", error)
          this.error = error

          this.isLoadingKey = false
        }
      )
    },

    fetchChatDocIds(chatKey = this.chatKey) {
      this.isLoadingDocIds = true
      this.error = null

      if (this.isFolder) {
        ApiService.fetchChatDocIds(
          {
            datasource_name: this.datasourceName,
            path: this.path,
            current_node: this.currentNode
          },
          (response) => {
            this.docIds[chatKey] = response
            this.isLoadingDocIds = false
          },
          (error) => {
            console.error("Error fetching chat doc_ids", error)
            this.error = error

            this.docIds[chatKey] = []
            this.isLoadingDocIds = false
          }
        )
      } else {
        this.docIds[chatKey] = [this.selectedFile.id]
        this.isLoadingDocIds = false
      }
    },

    reloadChat() {
      this.isDeleting = false
      this.chatKey = null
      this.fetchChatKey()
    },

    reloadPage() {
      window.location.reload()
    },

    handleAction(action) {
      action.action()
      this.drawerActionsIsOpen = false
    }
  }
}
</script>

<template>
  <div ref="drawer" class="drawer">
    <div class="drawer-header">
      <div class="drawer-title">{{ title }}</div>

      <span class="ml-auto">
        <dropdown-menu v-if="drawerActions.length">
          <dropdown-menu-trigger>
            <menu-icon />
          </dropdown-menu-trigger>

          <dropdown-menu-content align="end">
            <dropdown-menu-item
              class="drawer-actions__item"
              v-for="action in drawerActions"
              @click="handleAction(action)">
              <div class="drawer-actions__item-icon">
                <i :class="action.icon"></i>
              </div>
              <div class="drawer-actions__item-label">{{ action.label }}</div>
            </dropdown-menu-item>
          </dropdown-menu-content>
        </dropdown-menu>
      </span>
    </div>

    <div class="drawer-body">
      <div class="chat-drawer__error-container" v-if="error">
        <div class="chat-drawer__error-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
        <div class="chat-drawer__error-message">Si è verificato un errore durante il caricamento della chat</div>
        <Button type="button" @click="reloadPage">Ricarica</Button>
      </div>

      <Chat :chat-key="chatKey" :doc-ids="docIds[chatKey]" :is-loading="isLoading" v-else-if="chatKey" />
    </div>
  </div>
</template>

<style scoped>
.drawer {
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 700px;
}

.drawer-header {
  border-bottom: 1px solid #555;
  display: flex;
  flex: 0;
  padding: 1rem;
}

.drawer-body {
  flex: 1;
  padding: 1rem 1rem 0.3rem;
  overflow: auto;
}

.chat-drawer__loader {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.chat-drawer__error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 100%;
  width: 100%;
}

.chat-drawer__error-icon {
  font-size: 48px;
  color: #f00;
}

.chat-drawer__error-message {
  text-align: center;
}
</style>
